import { useMemo, useState } from "react";
import {
  TransactionEventViewDto,
  TransactionReadable,
  TransactionViewDto,
} from "../../../services/client";

const DEFAULT_PAGE_SIZE = 20;

// The optimisation view only has access to the raw, flat lifecycle feed
// (one row per state change, sharing a transactionId) rather than the
// pre-grouped `TransactionViewDto` the transactions endpoint returns. Group
// it client-side so both views can render through the same table.
function groupTransactionReadables(
  items: TransactionReadable[],
): TransactionViewDto[] {
  const byTransactionId = new Map<string, TransactionReadable[]>();
  for (const item of items) {
    const group = byTransactionId.get(item.transactionId);
    if (group) {
      group.push(item);
    } else {
      byTransactionId.set(item.transactionId, [item]);
    }
  }

  return [...byTransactionId.entries()].map(([transactionId, group]) => {
    const sorted = [...group].sort(
      (a, b) =>
        new Date(a.received_at ?? a.timestamp).getTime() -
        new Date(b.received_at ?? b.timestamp).getTime(),
    );
    const latest = sorted[sorted.length - 1];

    const events: TransactionEventViewDto[] = sorted.map((event) => ({
      event_name: event.transactionState,
      received_at: event.received_at ?? event.timestamp,
      transaction_state: event.transactionState,
      started_at: event.startTime,
      estimated_departure_time: event.estimatedDepartureTime ?? null,
      stop_time: event.stopTime ?? null,
      requested_min_energy:
        "requestedMinEnergy" in event ? event.requestedMinEnergy : null,
      max_power: "maxPower" in event ? event.maxPower : null,
      profile_id: event.profile_id ?? "",
      badge_id: null,
    }));

    return {
      asset_id: latest.assetId,
      transaction_id: transactionId,
      transaction_state: latest.transactionState,
      started_at: latest.startTime,
      estimated_departure_time: latest.estimatedDepartureTime ?? null,
      stop_time: latest.stopTime ?? null,
      requested_min_energy:
        "requestedMinEnergy" in latest ? latest.requestedMinEnergy : null,
      max_power: "maxPower" in latest ? latest.maxPower : null,
      profile_id: latest.profile_id ?? "",
      badge_id: null,
      event_count: events.length,
      latest_event_name: latest.transactionState,
      latest_event_received_at: latest.received_at ?? latest.timestamp,
      events,
    };
  });
}

// Groups a flat feed of `TransactionReadable` lifecycle events into
// `TransactionViewDto`s and paginates the result client-side, so
// `SimpleTransactionsTable` can render through the same view as the
// server-paginated table.
export function useGroupedTransactions(
  transactions: TransactionReadable[],
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const groupedTransactions = useMemo(
    () => groupTransactionReadables(transactions),
    [transactions],
  );

  const pageTransactions = useMemo(
    () =>
      groupedTransactions.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
      ),
    [groupedTransactions, currentPage, pageSize],
  );

  return {
    pageTransactions,
    groupedTransactions,
    currentPage,
    pageSize,
    total: groupedTransactions.length,
    onPageChange: setCurrentPage,
  };
}
